import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  getUsersFromFirebase, 
  calculateAnalytics, 
  getLocationData, 
  getUserGrowthData,
  scheduleExport,
  FirebaseUser,
  FirebaseAnalytics
} from '../services/firebaseService';

interface User {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  businessType: string;
  role: string;
  country: string;
  state: string;
  createdAt: string;
  results?: any;
}

interface Analytics {
  totalUsers: number;
  completedTests: number;
  averageScore: number;
  scoreImprovement: number;
  userGrowth: {
    thisWeek: number;
    thisMonth: number;
    thisYear: number;
  };
  readinessDistribution: {
    ready: number;
    almostReady: number;
    notReady: number;
  };
  userGrowthData: Array<{
    date: string;
    users: number;
    completions: number;
  }>;
  locationData: Array<{
    country: string;
    state: string;
    count: number;
    averageScore: number;
  }>;
  categoryPerformance: Array<{
    category: string;
    averageScore: number;
    completionRate: number;
  }>;
}

interface ExportData {
  users: Array<Record<string, string>>;
  analytics: Record<string, string | number>;
  locations: any[];
  categories: any[];
  selectedDataTypes: {
    users: boolean;
    analytics: boolean;
    locations: boolean;
    categories: boolean;
  };
}

interface AdminState {
  users: User[];
  analytics: Analytics;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  
  // Actions
  setAuthenticated: (authenticated: boolean) => void;
  fetchUsers: () => Promise<void>;
  fetchAnalytics: () => Promise<void>;
  exportToGoogleSheets: () => Promise<{ success: boolean; spreadsheetUrl?: string; error?: string }>;
  scheduleDataExport: (frequency: 'weekly' | 'monthly', day: string, time: string) => Promise<void>;
  addUser: (user: Omit<User, 'id' | 'createdAt'>) => void;
  updateUser: (id: string, updates: Partial<User>) => void;
  deleteUser: (id: string) => void;
}

const convertFirebaseUserToUser = (firebaseUser: FirebaseUser): User => ({
  id: firebaseUser.id || '',
  fullName: firebaseUser.fullName,
  email: firebaseUser.email,
  phone: firebaseUser.phone,
  businessType: firebaseUser.businessType,
  role: firebaseUser.role,
  country: firebaseUser.country,
  state: firebaseUser.state,
  createdAt: firebaseUser.createdAt.toDate().toISOString(),
  results: firebaseUser.results
});

let isExporting = false;

const exportToGoogleSheets = async (data: ExportData) => {
  if (isExporting) {
    console.log('Export already in progress, skipping duplicate request');
    return;
  }

  try {
    isExporting = true;
    console.log('Preparing data for export:', {
      usersCount: data.users?.length,
      hasAnalytics: !!data.analytics,
      analyticsKeys: data.analytics ? Object.keys(data.analytics) : []
    });

    // Use the relative API path
    const response = await fetch('/api/export-to-sheets', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Server response:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(errorData?.error || 'Failed to export to Google Sheets');
    }

    const result = await response.json();
    console.log('Export successful:', result);
    return result;
  } catch (error) {
    console.error('Export to Google Sheets failed:', error);
    throw new Error('Failed to export to Google Sheets');
  } finally {
    isExporting = false;
  }
};

export const useAdminStore = create<AdminState>()(
  persist(
    (set, get) => ({
      users: [],
      analytics: {
        totalUsers: 0,
        completedTests: 0,
        averageScore: 0,
        scoreImprovement: 0,
        userGrowth: { thisWeek: 0, thisMonth: 0, thisYear: 0 },
        readinessDistribution: { ready: 0, almostReady: 0, notReady: 0 },
        userGrowthData: [],
        locationData: [],
        categoryPerformance: []
      },
      isLoading: false,
      error: null,
      isAuthenticated: false,
      
      setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),
      
      fetchUsers: async () => {
        set({ isLoading: true, error: null });
        try {
          const firebaseUsers = await getUsersFromFirebase();
          const users = firebaseUsers.map(convertFirebaseUserToUser);
          set({ users, isLoading: false });
        } catch (error) {
          console.error('Error fetching users:', error);
          set({ error: 'Failed to fetch users', isLoading: false });
        }
      },
      
      fetchAnalytics: async () => {
        set({ isLoading: true, error: null });
        try {
          const [analyticsData, locationData, growthData] = await Promise.all([
            calculateAnalytics(),
            getLocationData(),
            getUserGrowthData()
          ]);
          
          // Calculate growth percentages
          const currentMonth = new Date().getMonth();
          const currentYear = new Date().getFullYear();
          
          const thisMonthGrowth = Math.floor(Math.random() * 20) + 5; // Placeholder
          const thisWeekGrowth = Math.floor(Math.random() * 10) + 2; // Placeholder
          const thisYearGrowth = Math.floor(Math.random() * 100) + 20; // Placeholder
          
          const analytics: Analytics = {
            totalUsers: analyticsData.totalUsers,
            completedTests: analyticsData.completedTests,
            averageScore: analyticsData.averageScore,
            scoreImprovement: Math.floor(Math.random() * 10) - 5, // Placeholder
            userGrowth: {
              thisWeek: thisWeekGrowth,
              thisMonth: thisMonthGrowth,
              thisYear: thisYearGrowth
            },
            readinessDistribution: analyticsData.readinessDistribution,
            userGrowthData: growthData,
            locationData,
            categoryPerformance: Array.from({ length: 10 }, (_, i) => ({
              category: `Category ${i + 1}`,
              averageScore: Math.floor(Math.random() * 40) + 60,
              completionRate: Math.floor(Math.random() * 30) + 70
            }))
          };
          
          set({ analytics, isLoading: false });
        } catch (error) {
          console.error('Error fetching analytics:', error);
          set({ error: 'Failed to fetch analytics', isLoading: false });
        }
      },
      
      exportToGoogleSheets: async () => {
        try {
          const { users, analytics } = get();
          
          console.log('Preparing data for export:', {
            usersCount: users.length,
            hasAnalytics: !!analytics,
            analyticsKeys: Object.keys(analytics || {})
          });

          // Prepare data for Google Sheets
          const exportData = {
            users: users.map(user => ({
              'Full Name': user.fullName,
              'Email': user.email,
              'Phone': user.phone,
              'Business Type': user.businessType,
              'Role': user.role,
              'Country': user.country,
              'State': user.state,
              'Registration Date': new Date(user.createdAt).toLocaleDateString(),
              'Test Completed': user.results ? 'Yes' : 'No',
              'Overall Score': user.results ? `${user.results.overallPercentage}%` : 'N/A',
              'Readiness Status': user.results ? user.results.overallStatus : 'N/A',
              'Completion Date': user.results ? new Date(user.results.completedAt).toLocaleDateString() : 'N/A'
            })),
            analytics: {
              'Total Users': analytics.totalUsers,
              'Completed Tests': analytics.completedTests,
              'Completion Rate': `${Math.round((analytics.completedTests / analytics.totalUsers) * 100)}%`,
              'Average Score': `${analytics.averageScore}%`,
              'Ready Percentage': `${analytics.readinessDistribution.ready}%`,
              'Almost Ready Percentage': `${analytics.readinessDistribution.almostReady}%`,
              'Not Ready Percentage': `${analytics.readinessDistribution.notReady}%`
            },
            locations: analytics.locationData,
            categories: analytics.categoryPerformance,
            selectedDataTypes: {
              users: true,
              analytics: true,
              locations: true,
              categories: true
            }
          };
          
          console.log('Sending export request to server');
          
          // Call the server endpoint to create Google Sheet
          const result = await exportToGoogleSheets(exportData);
          
          if (!result.success) {
            throw new Error(result.error || 'Failed to export to Google Sheets');
          }

          // Open the Google Sheet in a new tab
          window.open(result.spreadsheetUrl, '_blank');
          
          return result;
          
        } catch (error) {
          console.error('Export to Google Sheets failed:', error);
          throw new Error('Failed to export to Google Sheets');
        }
      },
      
      scheduleDataExport: async (frequency, day, time) => {
        try {
          await scheduleExport(frequency, day, time);
        } catch (error) {
          throw new Error('Failed to schedule export');
        }
      },
      
      addUser: (userData) => {
        const newUser: User = {
          ...userData,
          id: `user-${Date.now()}`,
          createdAt: new Date().toISOString()
        };
        
        set(state => ({
          users: [...state.users, newUser]
        }));
      },
      
      updateUser: (id, updates) => {
        set(state => ({
          users: state.users.map(user => 
            user.id === id ? { ...user, ...updates } : user
          )
        }));
      },
      
      deleteUser: (id) => {
        set(state => ({
          users: state.users.filter(user => user.id !== id)
        }));
      }
    }),
    {
      name: 'admin-store',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);